import {
  dateKeyOf,
  japaneseHolidayNameOf,
  monthMatrix,
  monthTitleOf,
  WEEKDAYS_JA,
  type AnniversaryIcon,
  type EventOccurrence,
} from "@couples/shared";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRef, type TouchEvent as ReactTouchEvent } from "react";

import { withAlpha } from "../lib/color";
import { swipeDirectionOf } from "../lib/media/swipe";
import { anniversaryIconComponentOf } from "../lib/schedule/anniversaryIcons";
import { laneOf } from "../lib/schedule/format";
import type { EventCategory } from "../lib/schedule/categories";

interface Props {
  year: number;
  month: number;
  todayKey: string;
  selectedDate: string | null;
  occurrencesByDate: Map<string, EventOccurrence[]>;
  anniversaryIconsInMonth: Map<string, AnniversaryIcon[]>;
  categories: EventCategory[] | undefined;
  selfId: string | undefined;
  selfColor: string;
  partnerColor: string;
  bothColor: string;
  onShiftMonth: (delta: number) => void;
  onOpenDate: (dateKey: string) => void;
}

/** 月間カレンダー。月送りヘッダー・曜日行・日付グリッド + スワイプでの月送り */
export default function MonthCalendarGrid({
  year,
  month,
  todayKey,
  selectedDate,
  occurrencesByDate,
  anniversaryIconsInMonth,
  categories,
  selfId,
  selfColor,
  partnerColor,
  bothColor,
  onShiftMonth,
  onOpenDate,
}: Props) {
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  function onTouchStart(e: ReactTouchEvent<HTMLDivElement>) {
    const touch = e.touches[0];
    if (touch === undefined) return;
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  }

  function onTouchEnd(e: ReactTouchEvent<HTMLDivElement>) {
    const start = touchStart.current;
    touchStart.current = null;
    const touch = e.changedTouches[0];
    if (start === null || touch === undefined) return;
    const direction = swipeDirectionOf(touch.clientX - start.x, touch.clientY - start.y);
    if (direction === "next") onShiftMonth(1);
    else if (direction === "previous") onShiftMonth(-1);
  }

  return (
    <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => {
            onShiftMonth(-1);
          }}
          aria-label="前の月"
        >
          <ChevronLeft className="size-[17px] text-[var(--color-sub)]" aria-hidden />
        </button>
        <h2 className="text-[14.5px] font-bold">{monthTitleOf(year, month)}</h2>
        <button
          type="button"
          onClick={() => {
            onShiftMonth(1);
          }}
          aria-label="次の月"
        >
          <ChevronRight className="size-[17px] text-[var(--color-sub)]" aria-hidden />
        </button>
      </div>

      <div className="mb-1 grid grid-cols-7">
        {WEEKDAYS_JA.map((weekday, index) => (
          <span
            key={weekday}
            className={`pb-0.5 text-center text-[10.5px] ${
              index === 0
                ? "text-[var(--color-holiday)]"
                : index === 6
                  ? "text-[var(--color-saturday)]"
                  : "text-[var(--color-sub)]"
            }`}
          >
            {weekday}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-[3px]">
        {monthMatrix(year, month).map((date, index) => {
          if (date === null) return <div key={index} />;
          const ds = dateKeyOf(date);
          const isToday = ds === todayKey;
          const isSelected = ds === selectedDate;
          const dayOccurrences = occurrencesByDate.get(ds) ?? [];
          const holidayName = japaneseHolidayNameOf(date);
          const isSunOrHoliday = date.getDay() === 0 || holidayName !== null;
          const isSaturday = date.getDay() === 6 && holidayName === null;
          const annivIcons = anniversaryIconsInMonth.get(ds) ?? [];
          const hasAnniv = annivIcons.length > 0;
          const shownEvents = dayOccurrences.slice(0, 2);
          const hiddenCount = dayOccurrences.length - shownEvents.length;
          return (
            <button
              key={ds}
              type="button"
              onClick={() => {
                onOpenDate(ds);
              }}
              title={holidayName ?? undefined}
              className={`relative flex min-h-[64px] flex-col items-center gap-[2px] rounded-lg border pt-1 pb-1 ${
                isSelected
                  ? "border-[var(--color-teal)]/40 bg-[var(--color-teal)]/10"
                  : hasAnniv
                    ? "border-[var(--color-gold)]/45 bg-gradient-to-br from-white to-[var(--color-gold)]/[0.16]"
                    : "border-[var(--color-line)]/70 bg-white"
              }`}
            >
              {hasAnniv && (
                <span className="absolute top-0.5 right-0.5 flex items-center gap-[2px]">
                  {annivIcons.map((icon, iconIndex) => {
                    const DayAnnivIcon = anniversaryIconComponentOf(icon);
                    return (
                      <span
                        key={iconIndex}
                        className="flex size-[13px] items-center justify-center rounded-full bg-[var(--color-gold)]/20"
                      >
                        <DayAnnivIcon className="size-[8px] text-[var(--color-plum)]" aria-hidden />
                      </span>
                    );
                  })}
                </span>
              )}
              <span
                className={`flex size-[20px] shrink-0 items-center justify-center text-[12px] font-medium [font-variant-numeric:tabular-nums] ${
                  isToday
                    ? "rounded-full bg-[var(--color-teal)] text-white"
                    : isSunOrHoliday
                      ? "text-[var(--color-holiday)]"
                      : isSaturday
                        ? "text-[var(--color-saturday)]"
                        : ""
                } ${hasAnniv && !isToday ? "rounded-full ring-1 ring-[var(--color-gold)]/70" : ""}`}
              >
                {date.getDate()}
              </span>
              <span className="flex w-full min-w-0 flex-col gap-[1.5px] px-[2px]">
                {shownEvents.map((occurrence) => {
                  const lane = laneOf(occurrence.event.assignedTo, selfId);
                  const laneColor =
                    lane === "both" ? bothColor : lane === "you" ? selfColor : partnerColor;
                  const category =
                    categories?.find((c) => c.id === occurrence.event.categoryId) ?? null;
                  return (
                    <span
                      key={occurrence.event.id}
                      className="flex w-full min-w-0 items-center gap-1 rounded-[3px] px-[3px] text-[8.5px] leading-[1.5] font-medium"
                      style={{
                        backgroundColor: withAlpha(laneColor, "1f"),
                        color: laneColor,
                      }}
                    >
                      {category !== null && (
                        <span
                          className="size-1 shrink-0 rounded-full"
                          style={{ backgroundColor: category.color }}
                          aria-hidden
                        />
                      )}
                      <span className="min-w-0 truncate">{occurrence.event.title}</span>
                    </span>
                  );
                })}
                {hiddenCount > 0 && (
                  <span className="block px-[3px] text-[7.5px] text-[var(--color-sub)]">
                    ほか{hiddenCount}件
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
